/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useState } from "react"

import { format } from "date-fns"
import { sv } from "date-fns/locale"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import type { ChatSessionModel } from "@/api/models"
import type { UseChatHistoryProps } from "@/features/chat/hooks/use-chat-history"
import type { ChatShareFilter } from "@/features/chat/utils/chat-sharing-state"
import type { ScrollAreaProps } from "@radix-ui/react-scroll-area"

import { ChevronDown, MessagesSquare, Search, Trash2, X } from "@/components/icons"
import { SidebarNavSection } from "@/components/sidebar/sidebar-nav-section"
import { SidebarSearchResultItem } from "@/components/sidebar/sidebar-search-result-item"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Spinner } from "@/components/ui/spinner"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useSidebar } from "@/components/ui/use-sidebar"
import { useRoutes } from "@/contexts/use-routes"
import { useChatHistory } from "@/features/chat/hooks/use-chat-history"
import { useStartNewChat } from "@/features/chat/hooks/use-start-new-chat"
import { getChatSharingState, matchesShareFilter } from "@/features/chat/utils/chat-sharing-state"
import { getSessionIcon } from "@/features/chat/utils/session-icon"
import { ShareFilterRow } from "@/features/sharing/components/share-filter-row"
import { useStoredShareFilter } from "@/features/sharing/hooks/use-stored-share-filter"
import { cn } from "@/lib/utils"
import { SettingsService } from "@/services/settings-service"

import { ChatHistoryItem } from "./chat-history-item"
import { ChatUpdatesStrip } from "./chat-updates-strip"

const DEFAULT_VISIBLE_COUNT = 5

type ChatHistoryProps = Omit<UseChatHistoryProps, "externalSearchTerm"> &
  ScrollAreaProps & {
    globalSearchTerm?: string
    headerActions?: React.ReactNode
    hideSharing?: boolean
    newChatButton?: React.ReactNode
    onCollapseChange?: (isOpen: boolean) => void
    processingSessionIds?: Set<string>
  }

export function ChatHistory({
  className,
  sessionType,
  useChatStore,
  projectId,
  globalSearchTerm = "",
  headerActions,
  hideSharing = false,
  newChatButton,
  onCollapseChange,
  processingSessionIds,
  syncRoute = false,
  ...props
}: ChatHistoryProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { getRouteChatPath } = useRoutes()
  const { state: sidebarState, isMobile } = useSidebar()
  const isCollapsedSidebar = sidebarState === "collapsed" && !isMobile
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null)
  const [localSearchTerm, setLocalSearchTerm] = useState("")
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [showAllItems, setShowAllItems] = useState(false)
  const [isCollapsedPopoverOpen, setIsCollapsedPopoverOpen] = useState(false)
  const [showAllInCollapsed, setShowAllInCollapsed] = useState(false)

  const currentUserOid = SettingsService.getInstance().claims.oid

  const effectiveSearchTerm = globalSearchTerm || localSearchTerm
  const isSearchingSessions = Boolean(effectiveSearchTerm)
  const favoritesSectionStorageKey = `sidebar-chat-history-favorites-open-${sessionType}${
    projectId ? `-${projectId}` : ""
  }`
  const shareFilterStorageKey = `sidebar-chats-share-filter-${sessionType}${
    projectId ? `-${projectId}` : ""
  }`
  const [isFavoritesSectionOpenStored, setIsFavoritesSectionOpenStored] = useState(() => {
    if (typeof window === "undefined") return true
    const stored = localStorage.getItem(favoritesSectionStorageKey)
    return stored !== null ? stored === "true" : true
  })

  // Persisted "All | Shared with me | Shared by me" filter (remembers last used per session type)
  const [shareFilterStored, setShareFilter] = useStoredShareFilter(shareFilterStorageKey)

  const {
    sessions,
    isLoadingSessions,
    currentSessionId,
    currentDropdownChatSession,
    handleSelectSession,
    handleDropdownMenuOpen,
    deleteChatSession,
    deleteChatSessions,
    selectedSessions,
    setSelectedSessions,
    showDeleteSessionAlertDialog,
    setShowDeleteSessionAlertDialog,
    showDeleteSessionsAlertDialog,
    setShowDeleteSessionsAlertDialog,
    deleteChatSessionMutation,
    deleteChatSessionsMutation,
    groupedFilteredSessionsByDate,
    filteredSessions,
    setSearchTerm,
    filteredFavoriteSessions,
    toggleFavoriteSession,
    isFavorited,
    selectMode,
    setSelectMode,
    startRenamingSession,
    cancelRenamingSession,
    renamingSessionId,
    renameChatSessionMutation,
    handleRenameSessionTitle,
  } = useChatHistory({
    sessionType,
    useChatStore,
    projectId,
    externalSearchTerm: globalSearchTerm,
    // The page owns route sync and hydration, so this list only navigates.
    // Only a sidebar serving a route with no page owner passes true (#3515).
    syncRoute,
  })

  const { handleSharedLeaveRedirect } = useStartNewChat({ useChatStore, sessionType })
  const chatStore = useChatStore()
  const unreadResponseSessionIds = chatStore((state) => state.unreadResponseSessionIds)
  const unreadResponseSessionIdSet = useMemo(
    () => new Set(unreadResponseSessionIds),
    [unreadResponseSessionIds]
  )

  useEffect(() => {
    if (typeof window === "undefined") {
      setIsFavoritesSectionOpenStored(true)
      return
    }
    const stored = localStorage.getItem(favoritesSectionStorageKey)
    setIsFavoritesSectionOpenStored(stored !== null ? stored === "true" : true)
  }, [favoritesSectionStorageKey])

  const setIsFavoritesSectionOpen = useCallback(
    (open: boolean) => {
      setIsFavoritesSectionOpenStored(open)
      if (typeof window !== "undefined") {
        localStorage.setItem(favoritesSectionStorageKey, String(open))
      }
    },
    [favoritesSectionStorageKey]
  )

  // Sharing is not surfaced at all when hideSharing is set (e.g. AI Parser):
  // the filter is forced to "all" and shared-with-me chats are excluded entirely.
  const shareFilter: ChatShareFilter = hideSharing ? "all" : shareFilterStored
  const showShareFilter = !hideSharing

  const passesShareFilter = useCallback(
    (session: ChatSessionModel) => {
      if (!session) return false
      if (hideSharing) {
        return session.currentUserRole !== "reader"
      }
      return matchesShareFilter(getChatSharingState(session, currentUserOid), shareFilter)
    },
    [hideSharing, currentUserOid, shareFilter]
  )

  const isFavoritesSectionOpen = isSearchingSessions ? true : isFavoritesSectionOpenStored
  const [isRecentSectionOpen, setIsRecentSectionOpen] = useState(true)
  const [groupOpenState, setGroupOpenState] = useState<Record<string, boolean>>({})

  // Base sessions visible to this surface, ignoring the share filter (used for empty-state checks).
  const baseSessions = useMemo(
    () => (hideSharing ? sessions.filter((s) => s?.currentUserRole !== "reader") : sessions),
    [sessions, hideSharing]
  )

  // All sessions matching search + active share filter (favourites included).
  const filteredForShare = useMemo(
    () => filteredSessions.filter((session) => passesShareFilter(session)),
    [filteredSessions, passesShareFilter]
  )

  const visibleFavoriteSessions = useMemo(
    () => filteredFavoriteSessions.filter((session) => passesShareFilter(session)),
    [filteredFavoriteSessions, passesShareFilter]
  )

  // Date-grouped sessions (owned + shared), filtered by the active share filter.
  const filteredGroupedSessions = useMemo(() => {
    const result: typeof groupedFilteredSessionsByDate = {}
    for (const [key, group] of Object.entries(groupedFilteredSessionsByDate)) {
      const sessionsInGroup = group.sessions.filter((session) => passesShareFilter(session))
      if (sessionsInGroup.length > 0) {
        result[key] = { ...group, sessions: sessionsInGroup }
      }
    }
    return result
  }, [groupedFilteredSessionsByDate, passesShareFilter])

  const totalSessionCount = filteredForShare.length

  const limitedGroupedSessions = useMemo(() => {
    if (showAllItems || effectiveSearchTerm) {
      return filteredGroupedSessions
    }

    let remaining = DEFAULT_VISIBLE_COUNT
    const limited: typeof filteredGroupedSessions = {}

    for (const [key, group] of Object.entries(filteredGroupedSessions)) {
      if (remaining <= 0) break

      const sessionsToShow = group.sessions.slice(0, remaining)
      if (sessionsToShow.length > 0) {
        limited[key] = {
          ...group,
          sessions: sessionsToShow,
        }
        remaining -= sessionsToShow.length
      }
    }

    return limited
  }, [filteredGroupedSessions, showAllItems, effectiveSearchTerm])

  const isGroupOpen = useCallback(
    (title: string) => (isSearchingSessions ? true : (groupOpenState[title] ?? true)),
    [groupOpenState, isSearchingSessions]
  )

  const setGroupOpen = useCallback((title: string, open: boolean) => {
    setGroupOpenState((prev) => ({ ...prev, [title]: open }))
  }, [])

  const allVisibleSessions = useMemo(() => {
    return [
      ...visibleFavoriteSessions,
      ...Object.values(limitedGroupedSessions).flatMap((group) => group.sessions),
    ]
  }, [visibleFavoriteSessions, limitedGroupedSessions])

  const handleSearchChange = (value: string) => {
    setLocalSearchTerm(value)
    setSearchTerm(value)
    setSelectedSessions([])
  }

  const handleSelectModeToggle = (session?: ChatSessionModel) => {
    setSelectMode(true)
    setSelectedSessions(session ? [session] : [])
  }

  const handleExitSelectMode = () => {
    setSelectMode(false)
    setSelectedSessions([])
  }

  const handleCollapsedSessionClick = useCallback(
    (session: ChatSessionModel) => {
      if (!session?.id) return
      void navigate(`${getRouteChatPath(session.sessionType ?? "open")}/${session.id}`)
      setIsCollapsedPopoverOpen(false)
    },
    [navigate, getRouteChatPath]
  )

  const handleOpenUpdate = useCallback(
    (session: ChatSessionModel) => {
      if (!session?.id) return
      void navigate(`${getRouteChatPath(session.sessionType ?? "open")}/${session.id}`)
    },
    [navigate, getRouteChatPath]
  )

  const MAX_COLLAPSED_FAVORITES = 3

  const collapsedFavorites = useMemo(() => {
    const validFavorites = visibleFavoriteSessions.filter(
      (s): s is NonNullable<typeof s> => s != null
    )
    if (effectiveSearchTerm || showAllInCollapsed) {
      return validFavorites
    }
    return validFavorites.slice(0, MAX_COLLAPSED_FAVORITES)
  }, [visibleFavoriteSessions, effectiveSearchTerm, showAllInCollapsed])

  const collapsedRecentSessions = useMemo(() => {
    const validSessions = filteredForShare.filter(
      (s): s is NonNullable<typeof s> => s != null && !isFavorited(s.id)
    )
    if (effectiveSearchTerm || showAllInCollapsed) {
      return validSessions
    }
    const remainingSlots = Math.max(0, DEFAULT_VISIBLE_COUNT - collapsedFavorites.length)
    return validSessions.slice(0, remainingSlots)
  }, [
    filteredForShare,
    isFavorited,
    collapsedFavorites.length,
    effectiveSearchTerm,
    showAllInCollapsed,
  ])

  const totalCollapsedCount = filteredForShare.filter((s) => s != null).length
  const visibleCollapsedCount = collapsedFavorites.length + collapsedRecentSessions.length
  const hasMoreItems = totalCollapsedCount > visibleCollapsedCount

  if (isCollapsedSidebar) {
    const hasNoResults =
      !isLoadingSessions &&
      baseSessions.length > 0 &&
      collapsedFavorites.length === 0 &&
      collapsedRecentSessions.length === 0

    return (
      <Popover
        open={isCollapsedPopoverOpen}
        onOpenChange={(open) => {
          setIsCollapsedPopoverOpen(open)
          if (!open) setShowAllInCollapsed(false)
        }}
      >
        <PopoverTrigger asChild>
          <button
            aria-label={t("chatsession.chats", { defaultValue: "Chats" })}
            type="button"
            className={cn(
              "group/nav-section hover:bg-sidebar-accent hover:text-sidebar-accent-foreground flex size-10 items-center justify-center rounded-md transition-colors duration-150",
              className
            )}
          >
            <MessagesSquare className="size-6" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="flex w-72 flex-col p-0"
          side="right"
          sideOffset={8}
        >
          <div className="border-b p-2">
            <div className="relative">
              <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
              <Input
                aria-label={t("chatsession.search_placeholder")}
                className="h-8 pl-9 text-sm"
                placeholder={t("chatsession.search_placeholder")}
                value={localSearchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={(e) => e.key === "Escape" && localSearchTerm && handleSearchChange("")}
              />
            </div>
          </div>
          {showShareFilter && (
            <div className="border-b px-1 py-1">
              <ShareFilterRow
                testIdPrefix="chats-share-filter"
                value={shareFilter}
                onChange={setShareFilter}
              />
            </div>
          )}
          <div className="relative flex min-h-0 flex-1 flex-col">
            {showAllInCollapsed && (
              <div className="from-popover pointer-events-none absolute top-0 right-0 left-0 z-10 h-4 bg-gradient-to-b to-transparent" />
            )}
            <div
              className={cn(
                "overflow-y-auto",
                showAllInCollapsed ? "max-h-[300px] pb-10" : "max-h-[300px]"
              )}
            >
              <div className="flex flex-col gap-2 p-1">
                {isLoadingSessions && (
                  <p className="animate-shimmer from-muted-foreground via-foreground to-muted-foreground bg-gradient-to-r bg-[length:200%_100%] bg-clip-text px-2 py-4 text-center text-xs text-transparent">
                    {t("chatsession.loading_sessions")}
                  </p>
                )}
                {!isLoadingSessions &&
                  baseSessions.length === 0 &&
                  visibleFavoriteSessions.length === 0 && (
                    <p className="text-muted-foreground px-2 py-4 text-center text-xs">
                      {t("chatsession.no_sessions")}
                    </p>
                  )}
                {hasNoResults && (
                  <p className="text-muted-foreground px-2 py-4 text-center text-xs">
                    {effectiveSearchTerm
                      ? t("chatsession.no_matches")
                      : t("chatsession.no_shared_chats", { defaultValue: "Nothing here yet" })}
                  </p>
                )}

                {collapsedFavorites.length > 0 && (
                  <Collapsible
                    open={isFavoritesSectionOpen}
                    onOpenChange={setIsFavoritesSectionOpen}
                  >
                    <section className="flex flex-col gap-1">
                      <CollapsibleTrigger
                        className="text-muted-foreground/70 hover:text-muted-foreground group/section flex w-full items-center gap-1 px-2 pt-1 text-[10px] font-semibold tracking-wider uppercase disabled:cursor-default disabled:opacity-70"
                        disabled={isSearchingSessions}
                        type="button"
                      >
                        {t("chatsession.favorites")}
                        <ChevronDown
                          className={cn(
                            "text-muted-foreground/70 group-hover/section:text-muted-foreground size-3.5 shrink-0 -translate-y-[2px] opacity-0 transition-transform duration-150 group-hover/section:opacity-100",
                            !isFavoritesSectionOpen && "-rotate-90"
                          )}
                        />
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        {collapsedFavorites.map((session) => (
                          <SidebarSearchResultItem
                            key={session.id}
                            icon={getSessionIcon(session)}
                            isActive={session.id === currentSessionId}
                            timestamp={session.updated}
                            title={session.title ?? ""}
                            isUnread={Boolean(
                              session.id && unreadResponseSessionIdSet.has(session.id)
                            )}
                            onClick={() => handleCollapsedSessionClick(session)}
                          />
                        ))}
                      </CollapsibleContent>
                    </section>
                  </Collapsible>
                )}

                {collapsedRecentSessions.length > 0 && (
                  <Collapsible
                    open={isSearchingSessions ? true : isRecentSectionOpen}
                    onOpenChange={setIsRecentSectionOpen}
                  >
                    <section className="flex flex-col gap-1">
                      <CollapsibleTrigger
                        className="text-muted-foreground/70 hover:text-muted-foreground group/section flex w-full items-center gap-1 px-2 pt-1 text-[10px] font-semibold tracking-wider uppercase disabled:cursor-default disabled:opacity-70"
                        disabled={isSearchingSessions}
                        type="button"
                      >
                        {t("chatsession.recent", { defaultValue: "Recent" })}
                        <ChevronDown
                          className={cn(
                            "text-muted-foreground/70 group-hover/section:text-muted-foreground size-3.5 shrink-0 -translate-y-[2px] opacity-0 transition-transform duration-150 group-hover/section:opacity-100",
                            !isRecentSectionOpen && "-rotate-90"
                          )}
                        />
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        {collapsedRecentSessions.map((session) => (
                          <SidebarSearchResultItem
                            key={session.id}
                            icon={getSessionIcon(session)}
                            isActive={session.id === currentSessionId}
                            timestamp={session.updated}
                            title={session.title ?? ""}
                            isUnread={Boolean(
                              session.id && unreadResponseSessionIdSet.has(session.id)
                            )}
                            onClick={() => handleCollapsedSessionClick(session)}
                          />
                        ))}
                      </CollapsibleContent>
                    </section>
                  </Collapsible>
                )}

                {hasMoreItems && !effectiveSearchTerm && !showAllInCollapsed && (
                  <Button
                    className="text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground mt-1 h-7 w-full justify-start px-3 text-xs"
                    variant="ghost"
                    onClick={() => setShowAllInCollapsed(true)}
                  >
                    {t("sidebar.see_all", { defaultValue: "See all" })} ({totalCollapsedCount})
                  </Button>
                )}
              </div>
            </div>
            {showAllInCollapsed && !effectiveSearchTerm && (
              <div className="pointer-events-none absolute right-0 bottom-0 left-0 z-10 flex flex-col">
                <div className="from-popover h-6 bg-gradient-to-t to-transparent" />
                <div className="bg-popover px-1 pb-1">
                  <Button
                    className="text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground pointer-events-auto h-7 w-full justify-start px-3 text-xs"
                    variant="ghost"
                    onClick={() => setShowAllInCollapsed(false)}
                  >
                    {t("sidebar.show_less", { defaultValue: "Show less" })}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  const shouldShowSelectModeHeader =
    selectMode && (baseSessions.length > 0 || visibleFavoriteSessions.length > 0)

  const searchHeaderAction = !globalSearchTerm ? (
    <Button
      aria-label={t("chatsession.search", { defaultValue: "Search" })}
      size="icon"
      variant="ghost"
      className={cn(
        "hover:bg-foreground/15 size-7 rounded-full p-0",
        localSearchTerm && "bg-sidebar-accent text-sidebar-accent-foreground"
      )}
      onClick={(event) => {
        event.stopPropagation()
        setIsSearchOpen(true)
      }}
    >
      <Search className="size-4" />
    </Button>
  ) : null

  const combinedHeaderActions = (
    <>
      {searchHeaderAction}
      {headerActions}
    </>
  )

  const getEmptyStateMessage = () => {
    if (effectiveSearchTerm) return t("chatsession.no_matches")
    if (shareFilter !== "all") {
      return t("chatsession.no_shared_chats", { defaultValue: "Nothing here yet" })
    }
    return t("chatsession.no_sessions")
  }

  return (
    <>
      <SidebarNavSection
        allItemsShown={showAllItems}
        className={cn("min-h-0 flex-1", className)}
        defaultExpanded={true}
        forceExpanded={!!globalSearchTerm}
        headerActions={combinedHeaderActions}
        icon={<MessagesSquare className="size-4" />}
        isSearchActive={isSearchOpen && !globalSearchTerm}
        searchPlaceholder={t("chatsession.search_placeholder")}
        searchTerm={localSearchTerm}
        stickyShowLess={true}
        storageKey={`sidebar-chat-history-collapsed-${sessionType}${projectId ? `-${projectId}` : ""}`}
        title={t("chatsession.chats", { defaultValue: "Chats" })}
        toggleTestId="chats-section-toggle"
        totalCount={totalSessionCount}
        visibleCount={DEFAULT_VISIBLE_COUNT}
        onCollapseChange={onCollapseChange}
        onSearchChange={handleSearchChange}
        onSearchClose={() => setIsSearchOpen(false)}
        onSeeAllClick={() => setShowAllItems(true)}
        onShowLessClick={() => setShowAllItems(false)}
      >
        {newChatButton}

        {showShareFilter && <ChatUpdatesStrip sessions={sessions} onOpen={handleOpenUpdate} />}

        {showShareFilter && (
          <ShareFilterRow
            testIdPrefix="chats-share-filter"
            value={shareFilter}
            onChange={setShareFilter}
          />
        )}

        {shouldShowSelectModeHeader && (
          <div className="mb-2 flex items-center gap-1.5 px-2 text-xs">
            <span className="text-muted-foreground min-w-0 shrink-0 font-medium tabular-nums">
              {selectedSessions.length}
            </span>
            <Button
              className="h-6 min-w-0 shrink px-2 text-xs"
              disabled={filteredForShare.length === 0}
              size="sm"
              variant="outline"
              onClick={() => {
                setSelectedSessions(
                  selectedSessions.length === filteredForShare.length ? [] : filteredForShare
                )
              }}
            >
              <span className="truncate">
                {selectedSessions.length === filteredForShare.length && filteredForShare.length > 0
                  ? t("chatsession.deselect_all", { defaultValue: "Deselect all" })
                  : t("chatsession.choose_all", { defaultValue: "Select all" })}
              </span>
            </Button>
            {selectedSessions.length > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    aria-label={t("chatsession.delete", { defaultValue: "Delete selected" })}
                    className="text-destructive size-6 shrink-0 p-0"
                    size="icon"
                    variant="ghost"
                    onClick={() => setShowDeleteSessionsAlertDialog(true)}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  {t("chatsession.delete", { defaultValue: "Delete selected" })}
                </TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  aria-label={t("common.cancel", { defaultValue: "Cancel" })}
                  className="size-6 shrink-0 p-0"
                  size="icon"
                  variant="ghost"
                  onClick={handleExitSelectMode}
                >
                  <X className="size-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {t("common.cancel", { defaultValue: "Cancel" })}
              </TooltipContent>
            </Tooltip>
          </div>
        )}

        <div className={cn("flex min-h-0 flex-col gap-1", className)} {...props}>
          {isLoadingSessions && (
            <div className="flex items-center justify-center py-8">
              <p className="animate-shimmer from-muted-foreground via-foreground to-muted-foreground bg-gradient-to-r bg-[length:200%_100%] bg-clip-text text-xs text-transparent">
                {t("chatsession.loading_sessions")}
              </p>
            </div>
          )}
          {!isLoadingSessions &&
            totalSessionCount === 0 &&
            visibleFavoriteSessions.length === 0 && (
              <div className="flex items-center justify-center py-8">
                <p className="text-muted-foreground text-xs">{getEmptyStateMessage()}</p>
              </div>
            )}

          {(Object.keys(limitedGroupedSessions).length > 0 ||
            visibleFavoriteSessions.length > 0) && (
            <section className="flex flex-col gap-4">
              {visibleFavoriteSessions.length > 0 && (
                <Collapsible open={isFavoritesSectionOpen} onOpenChange={setIsFavoritesSectionOpen}>
                  <section className="flex flex-col gap-1">
                    <CollapsibleTrigger
                      className="text-muted-foreground/70 hover:text-muted-foreground group/section flex w-full items-center gap-1 px-2 text-[10px] font-semibold tracking-wider uppercase disabled:cursor-default disabled:opacity-70"
                      disabled={isSearchingSessions}
                      type="button"
                    >
                      {t("chatsession.favorites")}
                      <ChevronDown
                        className={cn(
                          "text-muted-foreground/70 group-hover/section:text-muted-foreground size-3.5 shrink-0 -translate-y-[2px] opacity-0 transition-transform duration-150 group-hover/section:opacity-100",
                          !isFavoritesSectionOpen && "-rotate-90"
                        )}
                      />
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="flex flex-col">
                        {visibleFavoriteSessions.map((session, index) => (
                          <ChatHistoryItem
                            key={session?.id}
                            allVisibleSessions={allVisibleSessions}
                            chatStore={chatStore}
                            currentSessionId={currentSessionId}
                            hideSharing={hideSharing}
                            isFavorited={isFavorited(session?.id)}
                            lastSelectedIndex={lastSelectedIndex}
                            navigateOnSelect={true}
                            renameIsPending={renameChatSessionMutation.isPending}
                            renamingSessionId={renamingSessionId}
                            selectedSessions={selectedSessions}
                            selectMode={selectMode}
                            session={session}
                            sessionIndex={index}
                            sessionType={sessionType}
                            setLastSelectedIndex={setLastSelectedIndex}
                            setSelectedSessions={setSelectedSessions}
                            setShowDeleteSessionAlertDialog={setShowDeleteSessionAlertDialog}
                            toggleFavoriteSession={toggleFavoriteSession}
                            isProcessing={
                              session?.id ? processingSessionIds?.has(session.id) : false
                            }
                            isUnread={Boolean(
                              session?.id && unreadResponseSessionIdSet.has(session.id)
                            )}
                            onCancelRenaming={cancelRenamingSession}
                            onDropdownMenuOpen={handleDropdownMenuOpen}
                            onRenameSession={handleRenameSessionTitle}
                            onSelectModeToggle={handleSelectModeToggle}
                            onSelectSession={handleSelectSession}
                            onSharedLeaveRedirect={handleSharedLeaveRedirect}
                            onStartRenaming={startRenamingSession}
                          />
                        ))}
                      </div>
                    </CollapsibleContent>
                  </section>
                </Collapsible>
              )}
              {Object.values(limitedGroupedSessions).map((group) => {
                const groupIsOpen = isGroupOpen(group.title)
                return (
                  <Collapsible
                    key={group.title}
                    open={groupIsOpen}
                    onOpenChange={(open) => setGroupOpen(group.title, open)}
                  >
                    <section className="flex flex-col gap-1">
                      <CollapsibleTrigger
                        className="text-muted-foreground/70 hover:text-muted-foreground group/section flex w-full items-center gap-1 px-2 text-[10px] font-semibold tracking-wider uppercase disabled:cursor-default disabled:opacity-70"
                        disabled={isSearchingSessions}
                        type="button"
                      >
                        {group.title}
                        <ChevronDown
                          className={cn(
                            "text-muted-foreground/70 group-hover/section:text-muted-foreground size-3.5 shrink-0 -translate-y-[2px] opacity-0 transition-transform duration-150 group-hover/section:opacity-100",
                            !groupIsOpen && "-rotate-90"
                          )}
                        />
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="flex flex-col">
                          {group.sessions.map((session, groupIndex) => {
                            const globalIndex =
                              visibleFavoriteSessions.length +
                              groupIndex +
                              Object.values(limitedGroupedSessions)
                                .slice(0, Object.values(limitedGroupedSessions).indexOf(group))
                                .reduce((acc, g) => acc + g.sessions.length, 0)

                            return (
                              <div
                                key={session?.id}
                                className={globalIndex < 10 ? "sidebar-stagger-item" : ""}
                                style={
                                  globalIndex < 10
                                    ? ({ "--stagger-index": globalIndex } as React.CSSProperties)
                                    : undefined
                                }
                              >
                                <ChatHistoryItem
                                  allVisibleSessions={allVisibleSessions}
                                  chatStore={chatStore}
                                  currentSessionId={currentSessionId}
                                  hideSharing={hideSharing}
                                  isFavorited={isFavorited(session?.id)}
                                  lastSelectedIndex={lastSelectedIndex}
                                  navigateOnSelect={true}
                                  renameIsPending={renameChatSessionMutation.isPending}
                                  renamingSessionId={renamingSessionId}
                                  selectedSessions={selectedSessions}
                                  selectMode={selectMode}
                                  session={session}
                                  sessionIndex={globalIndex}
                                  sessionType={sessionType}
                                  setLastSelectedIndex={setLastSelectedIndex}
                                  setSelectedSessions={setSelectedSessions}
                                  setShowDeleteSessionAlertDialog={setShowDeleteSessionAlertDialog}
                                  toggleFavoriteSession={toggleFavoriteSession}
                                  isProcessing={
                                    session?.id ? processingSessionIds?.has(session.id) : false
                                  }
                                  isUnread={Boolean(
                                    session?.id && unreadResponseSessionIdSet.has(session.id)
                                  )}
                                  onCancelRenaming={cancelRenamingSession}
                                  onDropdownMenuOpen={handleDropdownMenuOpen}
                                  onRenameSession={handleRenameSessionTitle}
                                  onSelectModeToggle={handleSelectModeToggle}
                                  onSelectSession={handleSelectSession}
                                  onSharedLeaveRedirect={handleSharedLeaveRedirect}
                                  onStartRenaming={startRenamingSession}
                                />
                              </div>
                            )
                          })}
                        </div>
                      </CollapsibleContent>
                    </section>
                  </Collapsible>
                )
              })}
            </section>
          )}
        </div>
      </SidebarNavSection>

      <Dialog open={showDeleteSessionAlertDialog} onOpenChange={setShowDeleteSessionAlertDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("chatsession.confirm_delete")}</DialogTitle>
            <DialogDescription>{currentDropdownChatSession?.title}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              disabled={deleteChatSessionMutation.isPending}
              variant="destructive"
              onClick={deleteChatSession}
            >
              {t("chatsession.delete")}
              {deleteChatSessionMutation.isPending && <Spinner />}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteSessionsAlertDialog} onOpenChange={setShowDeleteSessionsAlertDialog}>
        <DialogContent className="flex max-h-[90vh] min-h-0 min-w-[300px] flex-1 flex-col rounded-sm sm:max-w-[90vw] lg:max-w-[900px]">
          <DialogHeader className="flex size-full min-h-0 flex-1 flex-col gap-2">
            <DialogTitle>{t("chatsession.confirm_delete_many")}</DialogTitle>
            <DialogDescription className="size-full max-h-[500px] overflow-y-auto">
              <ol className="list-decimal space-y-3 pl-8">
                {selectedSessions.map((session) => (
                  <li
                    key={session?.id}
                    className="border-border/50 bg-muted/40 flex flex-col space-y-1 rounded-md border px-3 py-2"
                  >
                    <span className="text-sm font-medium">{session?.title}</span>
                    <span className="text-muted-foreground text-xs">
                      {t("chatsession.last_modified")}
                      {session?.updated &&
                        format(new Date(session.updated), "yyyy-MM-dd HH:mm", {
                          locale: sv,
                        })}
                    </span>
                  </li>
                ))}
              </ol>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="min-h-0">
            <Button
              disabled={deleteChatSessionsMutation.isPending}
              variant="destructive"
              onClick={deleteChatSessions}
            >
              {t("chatsession.delete")}
              {deleteChatSessionsMutation.isPending && <Spinner />}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
